<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import TaxiPartnersAPI from 'dashboard/api/taxiPartners';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();

const partners = ref([]);
const loading = ref(false);
const searchQuery = ref('');
const partnerDialogRef = ref(null);

const form = ref({
  name: '',
  phone: '',
  address: '',
});

const persistedPartnerId = ref(null);
const trips = ref([]);
const tripDraft = ref({
  trip_on: '',
  notes: '',
});

const filteredPartners = computed(() => {
  if (!searchQuery.value.trim()) return partners.value;
  const q = searchQuery.value.toLowerCase();
  return partners.value.filter(p => {
    return (
      p.name?.toLowerCase().includes(q) ||
      p.phone?.toLowerCase().includes(q) ||
      p.address?.toLowerCase().includes(q) ||
      (p.trips || []).some(tr => tr.notes?.toLowerCase().includes(q))
    );
  });
});

const resetForm = () => {
  form.value = {
    name: '',
    phone: '',
    address: '',
  };
  persistedPartnerId.value = null;
  trips.value = [];
  tripDraft.value = { trip_on: '', notes: '' };
};

const fetchPartners = async () => {
  loading.value = true;
  try {
    const { data } = await TaxiPartnersAPI.getAll();
    partners.value = data;
  } catch {
    useAlert(t('TAXIS.PARTNERS.API.ERROR_FETCH'));
  } finally {
    loading.value = false;
  }
};

onMounted(fetchPartners);

const openCreate = () => {
  resetForm();
  partnerDialogRef.value?.open();
};

const openEdit = partner => {
  resetForm();
  persistedPartnerId.value = partner.id;
  form.value = {
    name: partner.name || '',
    phone: partner.phone || '',
    address: partner.address || '',
  };
  trips.value = [...(partner.trips || [])];
  partnerDialogRef.value?.open();
};

const onDialogClose = () => {
  resetForm();
};

const closeDialog = () => {
  partnerDialogRef.value?.close();
};

const savePartner = async () => {
  if (!form.value.name.trim()) {
    useAlert(t('TAXIS.PARTNERS.VALIDATION.NAME_REQUIRED'));
    return;
  }
  try {
    const payload = {
      name: form.value.name.trim(),
      phone: form.value.phone?.trim() || '',
      address: form.value.address?.trim() || '',
    };
    if (persistedPartnerId.value) {
      await TaxiPartnersAPI.update(persistedPartnerId.value, payload);
      useAlert(t('TAXIS.PARTNERS.API.SUCCESS_UPDATE'));
    } else {
      const { data } = await TaxiPartnersAPI.create(payload);
      persistedPartnerId.value = data.id;
      trips.value = data.trips || [];
      useAlert(t('TAXIS.PARTNERS.API.SUCCESS_CREATE'));
    }
    await fetchPartners();
  } catch {
    useAlert(t('TAXIS.PARTNERS.API.ERROR_SAVE'));
  }
};

const addTrip = async () => {
  if (!persistedPartnerId.value) {
    useAlert(t('TAXIS.PARTNERS.TRIPS.SAVE_PARTNER_FIRST'));
    return;
  }
  if (!tripDraft.value.trip_on) {
    useAlert(t('TAXIS.PARTNERS.TRIPS.DATE_REQUIRED'));
    return;
  }
  try {
    const { data } = await TaxiPartnersAPI.createTrip(
      persistedPartnerId.value,
      {
        trip_on: tripDraft.value.trip_on,
        notes: tripDraft.value.notes?.trim() || '',
      }
    );
    trips.value = [data, ...trips.value].sort((a, b) =>
      String(b.trip_on).localeCompare(String(a.trip_on))
    );
    tripDraft.value = { trip_on: '', notes: '' };
    useAlert(t('TAXIS.PARTNERS.TRIPS.API_SUCCESS_ADD'));
    await fetchPartners();
  } catch {
    useAlert(t('TAXIS.PARTNERS.TRIPS.API_ERROR_ADD'));
  }
};

const removeTrip = async trip => {
  if (!persistedPartnerId.value) return;
  // eslint-disable-next-line no-alert, no-restricted-globals
  if (!confirm(t('TAXIS.PARTNERS.TRIPS.CONFIRM_DELETE'))) return;
  try {
    await TaxiPartnersAPI.deleteTrip(persistedPartnerId.value, trip.id);
    trips.value = trips.value.filter(tr => tr.id !== trip.id);
    useAlert(t('TAXIS.PARTNERS.TRIPS.API_SUCCESS_DELETE'));
    await fetchPartners();
  } catch {
    useAlert(t('TAXIS.PARTNERS.TRIPS.API_ERROR_DELETE'));
  }
};

const removePartner = async partner => {
  // eslint-disable-next-line no-alert, no-restricted-globals
  if (!confirm(t('TAXIS.PARTNERS.CONFIRM_DELETE'))) return;
  try {
    await TaxiPartnersAPI.delete(partner.id);
    useAlert(t('TAXIS.PARTNERS.API.SUCCESS_DELETE'));
    await fetchPartners();
  } catch {
    useAlert(t('TAXIS.PARTNERS.API.ERROR_DELETE'));
  }
};

const formatDate = d => {
  if (!d) return t('TAXIS.HISTORY.DASH');
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return String(d);
  }
};

const addressPreview = p => {
  const a = p.address?.trim();
  if (!a) return t('TAXIS.HISTORY.DASH');
  return a.length > 48 ? `${a.slice(0, 45)}…` : a;
};

const lastTripOn = p => {
  const list = p.trips || [];
  if (!list.length) return t('TAXIS.HISTORY.DASH');
  const sorted = [...list].sort((a, b) =>
    String(b.trip_on).localeCompare(String(a.trip_on))
  );
  return formatDate(sorted[0].trip_on);
};

const tripsCount = p => (p.trips || []).length;
</script>

<template>
  <div class="flex flex-1 flex-col overflow-auto px-6 py-8">
    <div class="mx-auto flex w-full max-w-5xl flex-1 flex-col gap-6">
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div class="flex items-center gap-3">
            <div
              class="flex size-10 items-center justify-center rounded-xl bg-gradient-to-br from-n-teal-4/50 to-n-teal-5/40 ring-1 ring-n-teal-6/60"
            >
              <Icon icon="i-lucide-handshake" class="size-5 text-n-teal-11" />
            </div>
            <h1 class="text-xl font-semibold text-n-slate-12">
              {{ t('TAXIS.PARTNERS.TITLE') }}
            </h1>
          </div>
          <p class="mt-2 text-sm text-n-slate-11">
            {{ t('TAXIS.PARTNERS.SUBTITLE') }}
          </p>
        </div>
        <Button
          :label="t('TAXIS.PARTNERS.ADD_BUTTON')"
          icon="i-lucide-plus"
          size="sm"
          color="teal"
          @click="openCreate"
        />
      </div>

      <div class="max-w-sm">
        <Input
          v-model="searchQuery"
          type="search"
          :placeholder="t('TAXIS.PARTNERS.SEARCH_PLACEHOLDER')"
          :custom-input-class="[
            'h-9 [&:not(.focus)]:!border-transparent [&:not(.focus)]:!outline-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-9',
          ]"
        >
          <template #prefix>
            <Icon
              icon="i-lucide-search"
              class="pointer-events-none absolute top-1/2 size-4 -translate-y-1/2 text-n-slate-11 ltr:left-2.5 rtl:right-2.5"
            />
          </template>
        </Input>
      </div>

      <div
        v-if="loading"
        class="flex flex-1 items-center justify-center py-20 text-n-slate-11"
      >
        <Spinner />
      </div>

      <div
        v-else-if="!partners.length"
        class="flex flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed border-n-teal-5/30 bg-gradient-to-br from-n-teal-1/30 to-transparent py-20 text-center"
      >
        <div
          class="flex size-20 items-center justify-center rounded-2xl bg-gradient-to-br from-n-teal-4/40 to-n-teal-5/30 ring-4 ring-n-teal-4/20"
        >
          <Icon icon="i-lucide-handshake" class="size-10 text-n-teal-11" />
        </div>
        <div>
          <p class="text-lg font-semibold text-n-slate-12">
            {{ t('TAXIS.PARTNERS.EMPTY_TITLE') }}
          </p>
          <p class="mt-2 max-w-sm text-sm text-n-slate-11">
            {{ t('TAXIS.PARTNERS.EMPTY_SUBTITLE') }}
          </p>
        </div>
        <Button
          :label="t('TAXIS.PARTNERS.ADD_BUTTON')"
          icon="i-lucide-plus"
          size="md"
          color="teal"
          @click="openCreate"
        />
      </div>

      <div
        v-else
        class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-1"
      >
        <div class="overflow-x-auto">
          <table class="w-full min-w-[900px] text-left text-sm">
            <thead
              class="border-b-2 border-n-teal-6/30 bg-gradient-to-r from-n-teal-2/40 to-n-teal-3/20 text-xs font-semibold uppercase tracking-wide text-n-slate-12"
            >
              <tr>
                <th class="px-4 py-3.5">
                  <div class="flex items-center gap-1.5">
                    <Icon
                      icon="i-lucide-handshake"
                      class="size-3.5 text-n-teal-10"
                    />
                    {{ t('TAXIS.PARTNERS.COLUMNS.NAME') }}
                  </div>
                </th>
                <th class="px-4 py-3.5">
                  <div class="flex items-center gap-1.5">
                    <Icon
                      icon="i-lucide-phone"
                      class="size-3.5 text-n-blue-10"
                    />
                    {{ t('TAXIS.PARTNERS.COLUMNS.PHONE') }}
                  </div>
                </th>
                <th class="px-4 py-3.5">
                  <div class="flex items-center gap-1.5">
                    <Icon
                      icon="i-lucide-map-pin"
                      class="size-3.5 text-n-ruby-10"
                    />
                    {{ t('TAXIS.PARTNERS.COLUMNS.ADDRESS') }}
                  </div>
                </th>
                <th class="px-4 py-3.5">
                  <div class="flex items-center gap-1.5">
                    <Icon
                      icon="i-lucide-map"
                      class="size-3.5 text-n-purple-10"
                    />
                    {{ t('TAXIS.PARTNERS.COLUMNS.TRIPS_COUNT') }}
                  </div>
                </th>
                <th class="px-4 py-3.5">
                  <div class="flex items-center gap-1.5">
                    <Icon
                      icon="i-lucide-calendar"
                      class="size-3.5 text-n-amber-10"
                    />
                    {{ t('TAXIS.PARTNERS.COLUMNS.LAST_TRIP') }}
                  </div>
                </th>
                <th class="px-4 py-3.5 text-end">
                  {{ t('TAXIS.PARTNERS.COLUMNS.ACTIONS') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="partner in filteredPartners"
                :key="partner.id"
                class="transition-colors hover:bg-gradient-to-r hover:from-n-teal-2/10 hover:to-transparent"
              >
                <td class="px-4 py-3.5">
                  <div class="flex items-center gap-2">
                    <div
                      class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-n-teal-4/40 to-n-teal-5/30 ring-1 ring-n-teal-6/50"
                    >
                      <Icon
                        icon="i-lucide-building-2"
                        class="size-4 text-n-teal-11"
                      />
                    </div>
                    <span class="font-semibold text-n-slate-12">{{
                      partner.name
                    }}</span>
                  </div>
                </td>
                <td class="px-4 py-3.5">
                  <div
                    v-if="partner.phone"
                    class="inline-flex items-center gap-1.5 rounded-md bg-n-blue-2/50 px-2 py-1 text-sm text-n-blue-11"
                  >
                    <Icon icon="i-lucide-phone" class="size-3.5" />
                    {{ partner.phone }}
                  </div>
                  <span v-else class="text-n-slate-10">{{
                    t('TAXIS.HISTORY.DASH')
                  }}</span>
                </td>
                <td class="px-4 py-3.5">
                  <div
                    v-if="partner.address"
                    class="flex max-w-[12rem] items-start gap-1.5"
                  >
                    <Icon
                      icon="i-lucide-map-pin"
                      class="mt-0.5 size-3.5 shrink-0 text-n-ruby-10"
                    />
                    <span
                      class="text-sm text-n-slate-11"
                      :title="partner.address"
                    >
                      {{ addressPreview(partner) }}
                    </span>
                  </div>
                  <span v-else class="text-n-slate-10">{{
                    t('TAXIS.HISTORY.DASH')
                  }}</span>
                </td>
                <td class="px-4 py-3.5">
                  <div
                    class="inline-flex items-center gap-1.5 rounded-lg bg-n-purple-3/50 px-3 py-1.5 font-semibold text-n-purple-11 ring-1 ring-n-purple-6/50"
                  >
                    <Icon icon="i-lucide-map" class="size-4" />
                    <span class="text-sm">{{ tripsCount(partner) }}</span>
                  </div>
                </td>
                <td class="px-4 py-3.5">
                  <div
                    v-if="tripsCount(partner) > 0"
                    class="inline-flex items-center gap-1.5 rounded-md bg-n-amber-2/50 px-2 py-1 text-sm text-n-amber-11"
                  >
                    <Icon icon="i-lucide-calendar" class="size-3.5" />
                    {{ lastTripOn(partner) }}
                  </div>
                  <span v-else class="text-n-slate-10">{{
                    t('TAXIS.HISTORY.DASH')
                  }}</span>
                </td>
                <td class="px-4 py-3 text-end">
                  <div class="flex justify-end gap-1">
                    <button
                      type="button"
                      class="rounded-lg p-2 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                      :title="t('TAXIS.PARTNERS.EDIT')"
                      @click="openEdit(partner)"
                    >
                      <Icon icon="i-lucide-pencil" class="size-4" />
                    </button>
                    <button
                      type="button"
                      class="rounded-lg p-2 text-n-ruby-11 hover:bg-n-ruby-3/30"
                      :title="t('TAXIS.PARTNERS.DELETE')"
                      @click="removePartner(partner)"
                    >
                      <Icon icon="i-lucide-trash-2" class="size-4" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div
          v-if="partners.length && !filteredPartners.length"
          class="px-4 py-8 text-center text-sm text-n-slate-11"
        >
          {{ t('TAXIS.PARTNERS.NO_SEARCH_RESULTS') }}
        </div>
      </div>
    </div>

    <Dialog
      ref="partnerDialogRef"
      type="edit"
      :title="
        persistedPartnerId
          ? t('TAXIS.PARTNERS.DIALOG_EDIT_TITLE')
          : t('TAXIS.PARTNERS.DIALOG_CREATE_TITLE')
      "
      width="lg"
      :show-cancel-button="false"
      :show-confirm-button="false"
      overflow-y-auto
      @close="onDialogClose"
    >
      <div class="flex flex-col gap-4">
        <Input
          v-model="form.name"
          :label="t('TAXIS.PARTNERS.FORM.NAME')"
          :placeholder="t('TAXIS.PARTNERS.FORM.NAME_PLACEHOLDER')"
        />
        <Input
          v-model="form.phone"
          :label="t('TAXIS.PARTNERS.FORM.PHONE')"
          :placeholder="t('TAXIS.PARTNERS.FORM.PHONE_PLACEHOLDER')"
        />
        <TextArea
          v-model="form.address"
          :label="t('TAXIS.PARTNERS.FORM.ADDRESS')"
          :placeholder="t('TAXIS.PARTNERS.FORM.ADDRESS_PLACEHOLDER')"
          :max-length="2000"
          min-height="5rem"
        />

        <div class="border-t border-n-weak pt-4">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('TAXIS.PARTNERS.TRIPS.SECTION_TITLE') }}
          </h3>
          <p class="mt-1 text-xs text-n-slate-10">
            {{ t('TAXIS.PARTNERS.TRIPS.SECTION_HELP') }}
          </p>

          <div
            v-if="trips.length"
            class="mt-3 max-h-48 space-y-2 overflow-y-auto rounded-lg border border-n-weak bg-n-alpha-2/50 p-2"
          >
            <div
              v-for="tr in trips"
              :key="tr.id"
              class="flex items-start justify-between gap-2 rounded-md bg-n-solid-1 px-3 py-2 text-sm"
            >
              <div class="min-w-0 flex-1">
                <div class="font-medium text-n-slate-12">
                  {{ formatDate(tr.trip_on) }}
                </div>
                <div v-if="tr.notes" class="mt-0.5 text-n-slate-11">
                  {{ tr.notes }}
                </div>
              </div>
              <button
                type="button"
                class="shrink-0 rounded-md p-1.5 text-n-ruby-11 hover:bg-n-ruby-3/30"
                :title="t('TAXIS.PARTNERS.TRIPS.REMOVE')"
                @click="removeTrip(tr)"
              >
                <Icon icon="i-lucide-x" class="size-4" />
              </button>
            </div>
          </div>

          <div class="mt-3 grid gap-3 sm:grid-cols-2">
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('TAXIS.PARTNERS.TRIPS.TRIP_DATE') }}
              </label>
              <input
                v-model="tripDraft.trip_on"
                type="date"
                class="h-10 w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none focus:border-n-blue-8 focus:ring-1 focus:ring-n-blue-8"
              />
            </div>
            <div class="sm:col-span-2">
              <TextArea
                v-model="tripDraft.notes"
                :label="t('TAXIS.PARTNERS.TRIPS.NOTES')"
                :placeholder="t('TAXIS.PARTNERS.TRIPS.NOTES_PLACEHOLDER')"
                :max-length="2000"
                min-height="4rem"
              />
            </div>
          </div>
          <div class="mt-2">
            <Button
              type="button"
              variant="faded"
              color="slate"
              size="sm"
              icon="i-lucide-plus"
              :label="t('TAXIS.PARTNERS.TRIPS.ADD_TRIP')"
              @click="addTrip"
            />
          </div>
        </div>
      </div>
      <template #footer>
        <div class="flex w-full justify-end gap-2">
          <Button
            variant="faded"
            color="slate"
            type="button"
            :label="t('TAXIS.PARTNERS.CANCEL')"
            @click="closeDialog"
          />
          <Button
            type="button"
            :label="t('TAXIS.PARTNERS.SAVE')"
            @click="savePartner"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>
